import { useState, useEffect, useRef, FC } from "react";
import { playUISound, playVictorySound, playDefeatSound } from '../../utils/UISoundEffects';
import styles from "./GameEndPopover.module.css";
import { Mode } from "../../services/zodiacEngine/types";

interface GameEndPopoverProps {
  isGameOver: boolean;
  hasWon: boolean;
  isDraw?: boolean;
  duration?: number;
  mode?: Mode;
  isNNTestMode?: boolean;
}

const GameEndPopover: FC<GameEndPopoverProps> = ({
  isGameOver,
  hasWon,
  isDraw = false,
  duration = 2000,
  mode,
  isNNTestMode = false,
}) => {
  const [showPopover, setShowPopover] = useState<boolean>(false);
  // Use a ref so we can clear the timeout if the user manually closes
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Track if we've already shown the popover for this game to prevent re-showing
  const hasShownForGameRef = useRef<boolean>(false);

  // Reset the "has shown" flag when a new game starts (isGameOver goes from true to false)
  useEffect(() => {
    if (!isGameOver) {
      hasShownForGameRef.current = false;
    }
  }, [isGameOver]);

  useEffect(() => {
    // Don't show the popover in Dev mode, Sandbox mode, NN test mode, or TestAI mode
    // Also don't show if we've already shown it for this game
    if (
      isGameOver &&
      !hasShownForGameRef.current &&
      mode !== Mode.Dev &&
      mode !== Mode.Sandbox &&
      mode !== Mode.TestAI &&
      !isNNTestMode
    ) {
      hasShownForGameRef.current = true;
      setShowPopover(true);

      // The ending cue, fired where the popover itself is (see UISoundEffects).
      // This component already owns the once-per-game guard above, so the sound
      // inherits it and cannot double up.
      if (isDraw) playUISound();
      else if (hasWon) playVictorySound();
      else playDefeatSound();

      // Start the auto-close timer
      timerRef.current = setTimeout(() => {
        setShowPopover(false);
      }, duration);

      // Clean up when unmounting or re-running effect
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [isGameOver, duration, mode, isNNTestMode]);

  // Manually close the popover
  const handleClose = () => {
    setShowPopover(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  if (!showPopover) return null;

  // Determine result type
  const getResultClass = () => {
    if (isDraw) return styles.draw;
    if (hasWon) return styles.win;
    return styles.loss;
  };

  const getResultText = () => {
    if (isDraw) return "Draw";
    if (hasWon) return "Victory";
    return "Defeat";
  };

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.popover} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={handleClose}>
          &times;
        </button>
        <span className={`${styles.resultBadge} ${getResultClass()}`}>
          {getResultText()}
        </span>
      </div>
    </div>
  );
};

export default GameEndPopover;
